import React from "react";
import { ClipboardList, RefreshCw } from "lucide-react";
import { Button } from "../misc/Button";
import { cn } from "../../utils/classMerger";

interface MlsReviewBannerProps {
    pendingCount: number;
    archivedCount: number;
    onReview: () => void;
    // Re-runs a per-user, review-only MLS discovery (never auto-adds).
    onRefresh: () => void;
    refreshing?: boolean;
}

// Inline prompt shown with the Listings header. Consistent brand styling (gold badge + navy
// button). The message reflects the current data; the refresh button re-checks the MLS for new
// listings. Managing MLS details + property types lives inside the review modal. Visibility is
// owned by the parent (shown whenever the MLS agent ID is set).
const MlsReviewBanner: React.FC<MlsReviewBannerProps> = ({
    pendingCount,
    archivedCount,
    onReview,
    onRefresh,
    refreshing = false,
}) => {
    const message =
        pendingCount > 0 ? (
            <>
                <span className="font-bold">{pendingCount}</span>
                {` MLS listing${pendingCount === 1 ? "" : "s"} ready to review`}
            </>
        ) : archivedCount > 0 ? (
            <>
                <span className="font-bold">{archivedCount}</span>
                {` excluded MLS listing${
                    archivedCount === 1 ? "" : "s"
                } — review to re-add`}
            </>
        ) : (
            "MLS sync is on — no new listings to review right now"
        );

    return (
        <div className="flex w-fit max-w-full items-center gap-3 rounded-lg border border-gray-200 bg-white px-4 py-2.5 shadow-sm">
            <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-secondary">
                <ClipboardList className="h-[18px] w-[18px] text-primary" />
            </span>
            <span className="text-sm font-medium text-primary">{message}</span>
            <button
                type="button"
                aria-label="Refresh MLS listings"
                title="Check for new MLS listings"
                onClick={onRefresh}
                disabled={refreshing}
                className="shrink-0 rounded-md p-1.5 text-gray-400 transition-colors hover:bg-gray-100 hover:text-primary disabled:opacity-60"
            >
                <RefreshCw
                    className={cn("h-4 w-4", refreshing && "animate-spin")}
                />
            </button>
            <Button size="sm" onClick={onReview} className="ml-1 shrink-0">
                Review
            </Button>
        </div>
    );
};

export default MlsReviewBanner;
